// /settings → "Хүргэлтийн сувгийн нэршил" tab (spec §2.7a item 8 + §27.0 / Q14).
// Backed by the same governance-config endpoint — only the channelLabels
// JSON sub-object is touched here.

import { Button, Group, Skeleton, Stack, Text, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { TagIcon, } from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useGovernanceConfigQuery, useSettingsMutations } from "./hooks"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsButton, SettingsFields } from "./settings-controls"
import { HelpButtonGroup } from "@dehub/theme"

export function ChannelLabelsTab() {
  const { t } = useTranslation("settings")
  const DEFAULTS = {
    LOCAL_DELIVERY:  t("channelLabels.defaults.localDelivery"),
    SUPPLY_FREIGHT:  t("channelLabels.defaults.supplyFreight"),
    CUSTOMER_PICKUP: t("channelLabels.defaults.customerPickup"),
  }
  const { data, isLoading, error, mutate: reload } = useGovernanceConfigQuery()
  const mutations = useSettingsMutations()

  const form = useForm({
    initialValues: {
      LOCAL_DELIVERY: "",
      SUPPLY_FREIGHT: "",
      CUSTOMER_PICKUP: "",
    },
  })

  useEffect(() => {
    if (!data) return
    form.setValues({
      LOCAL_DELIVERY:  data.channelLabels?.LOCAL_DELIVERY  ?? "",
      SUPPLY_FREIGHT:  data.channelLabels?.SUPPLY_FREIGHT  ?? "",
      CUSTOMER_PICKUP: data.channelLabels?.CUSTOMER_PICKUP ?? "",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading) return <Skeleton height={120} radius="md" />
  if (error) {
    return (
      <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reload()} />
    )
  }

  const reset = () =>
    form.setValues({
      LOCAL_DELIVERY:  DEFAULTS.LOCAL_DELIVERY,
      SUPPLY_FREIGHT:  DEFAULTS.SUPPLY_FREIGHT,
      CUSTOMER_PICKUP: DEFAULTS.CUSTOMER_PICKUP,
    })

  return (
    <Stack gap="md" maw={520}>
      <Group gap="sm" align="center">
        <TagIcon size={22} />
        <Text fw={600} size="lg">{t("channelLabels.title")}</Text>
      </Group>
      <Text size="sm" c="gray.7">
        {t("channelLabels.description")}
      </Text>

      <SettingsFields code={SETTINGS_WRITE.governance}>
        <TextInput
          label={t("channelLabels.fields.localDelivery.label")}
          placeholder={DEFAULTS.LOCAL_DELIVERY}
          {...form.getInputProps("LOCAL_DELIVERY")}
        />
        <TextInput
          label={t("channelLabels.fields.supplyFreight.label")}
          placeholder={DEFAULTS.SUPPLY_FREIGHT}
          {...form.getInputProps("SUPPLY_FREIGHT")}
        />
        <TextInput
          label={t("channelLabels.fields.customerPickup.label")}
          placeholder={DEFAULTS.CUSTOMER_PICKUP}
          {...form.getInputProps("CUSTOMER_PICKUP")}
        />
      </SettingsFields>

      <Group justify="flex-end" gap="xs">
        <HelpButtonGroup helpKey="order.settings.action.reset-channel-labels">
          <Button variant="subtle" onClick={reset}>
            {t("channelLabels.actions.resetDefaults")}
          </Button>
        </HelpButtonGroup>
        <SettingsButton
          code={SETTINGS_WRITE.governance}
          helpKey="order.settings.action.save-channel-labels"
          loading={mutations.submitting}
          onClick={() =>
            mutations.governance.update({
              channelLabels: {
                LOCAL_DELIVERY:  form.values.LOCAL_DELIVERY,
                SUPPLY_FREIGHT:  form.values.SUPPLY_FREIGHT,
                CUSTOMER_PICKUP: form.values.CUSTOMER_PICKUP,
              },
            })
          }
        >
          {t("common.save")}
        </SettingsButton>
      </Group>
    </Stack>
  )
}
